import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DirectusService } from '../core/services/directus.service';
import {
  ImageCropUploadComponent,
  CropsRecord, ImageCropResult, CropMode
} from '../shared/image-crop-upload.component';

const PRESIDENT_MODES: CropMode[] = ['card', 'hero_desktop', 'hero_mobile', 'avatar'];

interface President {
  id: number;
  name: string;
  title: string;
  tagline: string | null;
  year: number;
  description: string | null;
  image: string | null;
  crops: CropsRecord | null;
  deleted: boolean;
}

interface PresidentForm {
  name: string;
  title: string;
  tagline: string;
  year: number;
  description: string;
  cropResult: ImageCropResult | null;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ImageCropUploadComponent],
  template: `
    <section class="pres-page">

      <!-- Header -->
      <header class="pres-header">
        <div>
          <h1>Presidents</h1>
          <p>Manage each year's Local President — photos and content are used across the website.</p>
        </div>
        <button class="btn-primary" (click)="openAdd()" [disabled]="saving">+ Add President</button>
      </header>

      <!-- Feedback -->
      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error"   *ngIf="errorMsg">{{ errorMsg }}</div>

      <!-- Loading -->
      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading…
      </div>

      <!-- Empty -->
      <div class="empty-state" *ngIf="!loading && presidents.length === 0">
        <div class="empty-icon">🏆</div>
        <h3>No presidents yet</h3>
        <p>Add the first president to get started.</p>
        <button class="btn-primary" (click)="openAdd()">+ Add President</button>
      </div>

      <!-- Grid -->
      <div class="pres-grid" *ngIf="!loading && presidents.length > 0">
        <div class="pres-card" *ngFor="let p of presidents">
          <div class="pres-photo">
            <img *ngIf="p.image" [src]="cardUrl(p)" [alt]="p.name"
                 (error)="imgErr($event)" />
            <div class="pres-photo-fallback" *ngIf="!p.image">{{ initials(p.name) }}</div>
          </div>
          <div class="pres-body">
            <div class="pres-year">{{ p.year }}</div>
            <div class="pres-name">{{ p.name }}</div>
            <div class="pres-title">{{ p.title }}</div>
            <div class="pres-tagline" *ngIf="p.tagline">"{{ p.tagline }}"</div>
          </div>
          <div class="pres-actions">
            <button class="btn-ghost xs" (click)="openEdit(p)">Edit</button>
            <button class="btn-ghost xs danger" (click)="confirmDelete(p)">Delete</button>
          </div>
        </div>
      </div>

      <!-- Edit / Add overlay -->
      <div class="overlay" *ngIf="formOpen" (click)="closeForm()">
        <div class="panel panel--xl" (click)="$event.stopPropagation()">
          <div class="panel-head">
            <h3>{{ editing ? 'Edit President' : 'Add President' }}</h3>
            <button class="panel-close" (click)="closeForm()">✕</button>
          </div>
          <div class="panel-body" *ngIf="form">

            <!-- Photo crop -->
            <div class="field-section">
              <label class="field-label">Photo</label>
              <app-image-crop-upload
                [imageId]="editing?.image ?? null"
                [existingCrops]="editing?.crops ?? null"
                [modes]="presidentModes"
                (change)="form.cropResult = $event">
              </app-image-crop-upload>
            </div>

            <div class="form-grid">
              <div class="field">
                <label class="field-label">Full name <span class="req">*</span></label>
                <input class="field-input" type="text" [(ngModel)]="form.name"
                       placeholder="e.g. Andre Yanuarsia" />
              </div>
              <div class="field">
                <label class="field-label">Year <span class="req">*</span></label>
                <input class="field-input" type="number" [(ngModel)]="form.year" min="2000" max="2099" />
              </div>
              <div class="field full">
                <label class="field-label">Title</label>
                <input class="field-input" type="text" [(ngModel)]="form.title"
                       placeholder="e.g. 2026 Local President" />
              </div>
              <div class="field full">
                <label class="field-label">Tagline</label>
                <input class="field-input" type="text" [(ngModel)]="form.tagline"
                       placeholder="e.g. Connect Grow Lead" />
              </div>
              <div class="field full">
                <label class="field-label">Description / bio</label>
                <textarea class="field-input" rows="5" [(ngModel)]="form.description"
                          placeholder="Short bio or message shown on the website…"></textarea>
              </div>
            </div>

          </div>
          <div class="panel-foot">
            <button class="btn-ghost" (click)="closeForm()">Cancel</button>
            <button class="btn-primary" (click)="save()"
                    [disabled]="saving || !form?.name || !form?.year">
              {{ saving ? 'Saving…' : (editing ? 'Save changes' : 'Add president') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete confirm -->
      <div class="overlay" *ngIf="deleteTarget" (click)="deleteTarget = null">
        <div class="panel panel--sm" (click)="$event.stopPropagation()">
          <div class="panel-head">
            <h3>Delete president?</h3>
            <button class="panel-close" (click)="deleteTarget = null">✕</button>
          </div>
          <div class="panel-body">
            <p>Remove <strong>{{ deleteTarget.name }} ({{ deleteTarget.year }})</strong> from the website?
              This cannot be undone.</p>
          </div>
          <div class="panel-foot">
            <button class="btn-ghost" (click)="deleteTarget = null">Cancel</button>
            <button class="btn-danger" (click)="doDelete()" [disabled]="saving">
              {{ saving ? 'Deleting…' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>

    </section>
  `,
  styles: [`
    .pres-page { padding: 2rem; max-width: 1200px; }

    /* Header */
    .pres-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .pres-header h1 { margin: 0 0 0.25rem; font-size: 1.75rem; }
    .pres-header p  { margin: 0; color: var(--app-muted); font-size: 0.875rem; }

    /* Feedback */
    .feedback {
      padding: 0.75rem 1rem;
      border-radius: var(--app-radius);
      margin-bottom: 1rem;
      font-size: 0.875rem;
    }
    .feedback.success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
    .feedback.error   { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

    /* Loading / empty */
    .loading-state { display: flex; align-items: center; gap: 0.5rem; color: var(--app-muted); padding: 2rem 0; }
    .spinner {
      width: 18px; height: 18px;
      border: 2px solid var(--app-border);
      border-top-color: var(--jci-blue);
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .empty-state { text-align: center; padding: 4rem 2rem; color: var(--app-muted); }
    .empty-icon  { font-size: 3rem; margin-bottom: 0.75rem; }
    .empty-state h3 { margin: 0 0 0.5rem; color: var(--app-muted); font-size: 1.1rem; }
    .empty-state p  { margin: 0 0 1rem; }

    /* Grid */
    .pres-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 1.25rem;
    }
    .pres-card {
      background: var(--app-surface);
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: var(--app-shadow);
    }
    .pres-photo {
      aspect-ratio: 4/5;
      background: var(--app-surface-soft);
      overflow: hidden;
      position: relative;
    }
    .pres-photo img {
      width: 100%; height: 100%;
      object-fit: cover;
      object-position: top;
      display: block;
    }
    .pres-photo-fallback {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 2rem; font-weight: 700;
      background: var(--jci-blue);
      color: #fff;
    }
    .pres-body { padding: 1rem; flex: 1; }
    .pres-year   { font-size: 0.75rem; color: var(--app-muted); margin-bottom: 0.25rem; }
    .pres-name   { font-weight: 700; font-size: 1rem; margin-bottom: 0.25rem; }
    .pres-title  { font-size: 0.8rem; color: var(--jci-blue); margin-bottom: 0.375rem; }
    .pres-tagline{ font-size: 0.78rem; color: var(--app-muted); font-style: italic; }
    .pres-actions {
      display: flex;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-top: 1px solid var(--app-border);
    }

    /* Overlay / panel */
    .overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.4);
      display: flex; align-items: center; justify-content: center;
      z-index: 100; padding: 1rem;
    }
    .panel {
      background: var(--app-surface);
      border-radius: var(--app-radius);
      box-shadow: 0 8px 32px rgba(0,0,0,0.18);
      display: flex; flex-direction: column;
      max-height: 92vh; overflow: hidden;
      width: 100%;
    }
    .panel--sm { max-width: 420px; }
    .panel--xl { max-width: 760px; }
    .panel-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid var(--app-border);
      flex-shrink: 0;
    }
    .panel-head h3 { margin: 0; font-size: 1.1rem; }
    .panel-close {
      background: none; border: none; cursor: pointer;
      font-size: 1.1rem; color: var(--app-muted); padding: 0.25rem;
    }
    .panel-body {
      padding: 1.5rem;
      overflow-y: auto;
      display: flex; flex-direction: column; gap: 1.25rem;
    }
    .panel-foot {
      display: flex; justify-content: flex-end; gap: 0.75rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--app-border);
      flex-shrink: 0;
    }

    /* Form */
    .field-section { display: flex; flex-direction: column; gap: 0.375rem; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
    .field { display: flex; flex-direction: column; gap: 0.375rem; }
    .field.full { grid-column: 1 / -1; }
    .field-label { font-size: 0.8rem; font-weight: 600; color: var(--app-muted); }
    .req { color: var(--app-danger); }
    .field-input {
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      background: var(--app-input-bg);
      font-size: 0.875rem; font-family: inherit;
      color: inherit;
      outline: none;
      transition: border-color 0.15s;
    }
    .field-input:focus { border-color: var(--jci-blue); }
    textarea.field-input { resize: vertical; }

    /* Buttons */
    .btn-primary {
      padding: 0.5rem 1.25rem;
      background: var(--jci-blue); color: #fff;
      border: none; border-radius: var(--app-radius);
      font-size: 0.875rem; font-weight: 600; cursor: pointer;
      font-family: inherit; transition: opacity 0.15s;
    }
    .btn-primary:hover:not(:disabled) { opacity: 0.88; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-ghost {
      padding: 0.5rem 1rem;
      background: transparent; color: inherit;
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      font-size: 0.875rem; cursor: pointer;
      font-family: inherit; transition: all 0.15s;
    }
    .btn-ghost:hover { border-color: var(--jci-blue); color: var(--jci-blue); }
    .btn-ghost.xs  { font-size: 0.8rem; padding: 0.3rem 0.75rem; }
    .btn-ghost.xs.danger { color: #dc2626; }
    .btn-ghost.xs.danger:hover { border-color: #dc2626; }
    .btn-danger {
      padding: 0.5rem 1.25rem;
      background: #dc2626; color: #fff;
      border: none; border-radius: var(--app-radius);
      font-size: 0.875rem; font-weight: 600; cursor: pointer;
      font-family: inherit; transition: opacity 0.15s;
    }
    .btn-danger:hover:not(:disabled) { opacity: 0.88; }
    .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
  `]
})
export class PresidentsComponent implements OnInit {
  readonly presidentModes: CropMode[] = PRESIDENT_MODES;

  loading  = false;
  saving   = false;
  successMsg = '';
  errorMsg   = '';

  presidents: President[] = [];

  formOpen = false;
  editing: President | null = null;
  form: PresidentForm | null = null;

  deleteTarget: President | null = null;

  constructor(private readonly directus: DirectusService) {}

  ngOnInit() { this.load(); }

  private async load() {
    this.loading = true;
    try {
      const rows = await this.directus.list('presidents', {
        'filter[deleted][_neq]': true,
        sort: '-year',
        limit: 50
      });
      this.presidents = rows.map(r => ({
        id:          r['id'] as number,
        name:        String(r['name'] ?? ''),
        title:       String(r['title'] ?? ''),
        tagline:     r['tagline'] as string | null,
        year:        r['year'] as number,
        description: r['description'] as string | null,
        image:       r['image'] as string | null,
        crops:       r['crops'] as CropsRecord | null,
        deleted:     Boolean(r['deleted'])
      }));
    } catch {
      this.errorMsg = 'Failed to load presidents.';
    } finally {
      this.loading = false;
    }
  }

  openAdd() {
    this.editing = null;
    this.form = { name: '', title: '', tagline: '', year: new Date().getFullYear(), description: '', cropResult: null };
    this.formOpen = true;
    this.clearMessages();
  }

  openEdit(p: President) {
    this.editing = p;
    this.form = { name: p.name, title: p.title, tagline: p.tagline ?? '', year: p.year, description: p.description ?? '', cropResult: null };
    this.formOpen = true;
    this.clearMessages();
  }

  closeForm() { this.formOpen = false; this.editing = null; this.form = null; }

  async save() {
    if (!this.form || this.saving) return;
    if (!this.form.name.trim() || !this.form.year) return;
    this.saving = true;
    this.clearMessages();
    try {
      let imageId: string | null = this.editing?.image ?? null;
      let crops:   CropsRecord | null = this.editing?.crops ?? null;

      const cr = this.form.cropResult;
      if (cr?.file) {
        const up = await this.directus.uploadFile(cr.file);
        imageId  = up['id'] as string;
      }
      if (cr?.crops && Object.keys(cr.crops).length) crops = cr.crops;

      const payload: Record<string, unknown> = {
        name:        this.form.name.trim(),
        title:       this.form.title.trim() || null,
        tagline:     this.form.tagline.trim() || null,
        year:        this.form.year,
        description: this.form.description.trim() || null,
        image:       imageId,
        crops
      };

      if (this.editing) {
        await this.directus.update('presidents', this.editing.id, payload);
        this.showSuccess('President updated.');
      } else {
        await this.directus.create('presidents', payload);
        this.showSuccess('President added.');
      }

      this.formOpen = false;
      await this.load();
    } catch {
      this.errorMsg = 'Failed to save.';
    } finally {
      this.saving = false;
    }
  }

  confirmDelete(p: President) { this.deleteTarget = p; this.clearMessages(); }

  async doDelete() {
    if (!this.deleteTarget || this.saving) return;
    this.saving = true;
    try {
      await this.directus.update('presidents', this.deleteTarget.id, { deleted: true });
      this.showSuccess('President removed.');
      this.deleteTarget = null;
      await this.load();
    } catch {
      this.errorMsg = 'Failed to delete.';
      this.deleteTarget = null;
    } finally {
      this.saving = false;
    }
  }

  cardUrl(p: President): string {
    return this.directus.croppedAssetUrl(p.image!, p.crops?.['card'], 400, 500);
  }

  initials(name: string): string {
    return name.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  }

  imgErr(e: Event) { (e.target as HTMLImageElement).style.display = 'none'; }

  private showSuccess(msg: string) { this.successMsg = msg; setTimeout(() => this.successMsg = '', 3500); }
  private clearMessages() { this.successMsg = ''; this.errorMsg = ''; }
}
